import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ArrowRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const highlights = [
  { name: 'School Dances', desc: 'Lively celebrations where students let loose, connect, and make memories together.' },
  { name: 'Field Days', desc: 'Action-packed days of games, teamwork, and friendly competition under the sun.' },
  { name: 'Spirit Weeks', desc: 'Spirited weeks filled with themed days and fun activities that bring everyone together.' },
];

const galleryImages = [
  '/assets/images/schoolspirit/IMG_2566.jpeg',
  '/assets/images/schoolspirit/IMG_2574.jpeg',
  '/assets/images/schoolspirit/IMG_2790.jpeg',
  '/assets/images/schoolspirit/IMG_3654.jpeg',
];

const SchoolSpiritPage = () => (
  <div className="min-h-screen bg-white">
    <Navbar />

    <main id="main-content" className="pt-0">
      {/* Hero */}
      <section
        className="relative pt-36 pb-20 bg-[#00213a] overflow-hidden"
      >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <span className="text-sm font-semibold text-white tracking-tight">Student Life</span>
            </div>
          </div>
          <h1 className="text-5xl lg:text-7xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            School Spirit
          </h1>
          <p className="text-2xl text-white/90 font-normal max-w-3xl leading-relaxed mx-auto">
            Roaring louder than ever, fueled by our proud mascot — the Lion. 🦁
          </p>
        </div>
      </section>

      {/* Section — image left, text right */}
      <section className="flex flex-col lg:flex-row min-h-[460px]">
        <div className="lg:w-1/2 h-64 lg:h-auto overflow-hidden">
          <img
            src="/assets/images/schoolspirit/20251028_081359.jpg"
            alt="PS 96 students showing school spirit"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="lg:w-1/2 bg-[#00213a] flex items-center px-10 py-14 lg:px-16">
          <div className="max-w-lg">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-[#fdbb2e] mb-3">Go Lions</p>
            <h2 className="text-3xl lg:text-4xl font-semibold text-white mb-6 leading-tight" style={{ letterSpacing: '-0.02em' }}>
              Pride That Roars.
            </h2>
            <p className="text-white/80 leading-relaxed text-lg">
              School spirit roars louder than ever, fueled by our proud mascot, the Lion! 🦁 Our students embrace the excitement of every event, from lively school dances to action packed field days and spirited weeks filled with fun activities.
            </p>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-20">
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdbb2e]/5 rounded-lg border border-[#fdbb2e]/30 mb-4">
            <span className="text-sm font-semibold text-[#00213a]">The Fun Stuff</span>
          </div>
          <h2 className="text-4xl font-semibold text-gray-900 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Where the Excitement Happens
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {highlights.map((item) => (
            <div key={item.name} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md hover:border-[#fdbb2e]/40 transition-all duration-200">
              <div className="w-8 h-8 rounded-lg bg-[#fdbb2e]/10 flex items-center justify-center mb-4">
                <svg className="w-4 h-4 text-[#00213a]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L23 12l-5.714 2.143L15 21l-2.286-6.857L7 12l5.714-2.143L15 3z" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">{item.name}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Photo Grid */}
      <section className="bg-[#00213a] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-[#fdbb2e] mb-6 text-center">Spirit in Action</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {galleryImages.map((src) => (
              <div key={src} className="aspect-square overflow-hidden rounded-xl">
                <img
                  src={src}
                  alt="School spirit at PS 96"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-semibold text-gray-900 mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Be Part of the Pride
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            From dances to field days, there's always something to cheer about at PS 96.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/picture-gallery"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#00213a] text-white font-semibold rounded-xl hover:bg-[#003060] transition-colors duration-200"
            >
              See the Gallery <ArrowRightIcon />
            </a>
            <a
              href="/contact-us"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-[#00213a] text-[#00213a] font-semibold rounded-xl hover:bg-[#00213a] hover:text-white transition-colors duration-200"
            >
              Contact Us <ArrowRightIcon />
            </a>
          </div>
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default SchoolSpiritPage;
